import {
    McCard,
    McButton,
    McIcon,
    McTag,
    McTooltip,
} from '@maersk-global/mds-react-wrapper';
import { McAccordion, McAccordionItem } from '@maersk-global/mds-react-wrapper/components-core';
import { ScheduleQueryResponse, TerminalRuntimeInfo } from '../../types';
import { formatCronExpression } from '../../utils/queryUtils';
import { formatJavaUtcDateTime } from '../../utils/formatters';

interface ScheduleCardProps {
    schedule: ScheduleQueryResponse;
    onEdit: (schedule: ScheduleQueryResponse) => void;
    onDelete: (scheduleId: string) => void;
    onToggleEnabled: (id: string, enabled: boolean) => void;
    onViewResults: (schedule: ScheduleQueryResponse) => void;
    isAdmin?: boolean;
}

export const ScheduleCard: React.FC<ScheduleCardProps> = ({
    schedule,
    onEdit,
    onDelete,
    onToggleEnabled,
    onViewResults,
    isAdmin = false,
}) => {
    // Helper to determine status appearance
    const getStatusAppearance = (status?: string) => {
        if (!status) return 'neutral';
        if (status.includes('SUCCESS')) return 'success';
        if (status.includes('PARTIAL')) return 'warning';
        if (status.includes('FAILED') || status.includes('ERROR')) return 'error';
        return 'info';
    };

    return (
        <McCard style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '8px', minWidth: 0 }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <h3 className="mds-headline--small-bold" style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, maxWidth: '100%' }}>
                            Schedule: {schedule.scheduleName || 'Untitled'}
                        </h3>
                        <McTag appearance={schedule.enabled ? 'success' : 'neutral'} fit="small" style={{ flexShrink: 0 }}>
                            {schedule.enabled ? 'Active' : 'Paused'}
                        </McTag>
                    </div>
                    <div style={{ color: 'var(--mds-color-neutral-text-secondary, #666)', fontSize: '0.9rem', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {schedule.description || 'No description'}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.9rem', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                    <McIcon icon="chart-line-up" color="var(--mds-color-neutral-icon, #666)" style={{ flexShrink: 0 }} />
                    <span style={{ color: '#666', flexShrink: 0 }}>Query:</span>
                    <McTag fit="small" appearance="neutral" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{schedule.queryId}</McTag>
                    <span style={{ fontSize: '0.8rem', color: '#666', flexShrink: 0 }}>(v{schedule.versionNumber})</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                    <McIcon icon="clock" color="var(--mds-color-neutral-icon, #666)" style={{ flexShrink: 0 }} />
                    <span style={{ color: '#666', flexShrink: 0 }}>Schedule:</span>
                    <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px', wordBreak: 'break-all' }}>{schedule.cronExpression}</code>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                    <McIcon icon="database" color="var(--mds-color-neutral-icon, #666)" style={{ flexShrink: 0 }} />
                    <span style={{ color: '#666', flexShrink: 0 }}>Terminals:</span>
                    <McTag fit="small" appearance="info" style={{ flexShrink: 0 }}>{schedule.terminalIds.length}</McTag>
                    {schedule.terminalIds.length <= 3 && (
                        <span style={{ color: '#666', overflowWrap: 'break-word', wordBreak: 'break-word' }}>{schedule.terminalIds.join(', ')}</span>
                    )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                    <McIcon icon="globe" color="var(--mds-color-neutral-icon, #666)" style={{ flexShrink: 0 }} />
                    <span style={{ color: '#666', flexShrink: 0 }}>Timezone:</span>
                    <McTag fit="small" appearance="neutral">
                        {schedule.timeMode === 'FIXED_TIMEZONE' ? (schedule.fixedTimezone || 'UTC') : 'Terminal local'}
                    </McTag>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                    <McIcon icon="calendar" color="var(--mds-color-neutral-icon, #666)" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <span style={{ color: '#666', flexShrink: 0 }}>Next run (your time):</span>
                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '4px', minWidth: 0 }}>
                        <span>{schedule.nextExecutionTimeUserLocal || 'Calculating...'}</span>
                        {schedule.nextExecutionTime && (
                            <span style={{ color: '#999', fontSize: '0.8rem' }}>(UTC: {new Date(schedule.nextExecutionTime).toLocaleString('en-GB', { timeZone: 'UTC' })})</span>
                        )}
                    </span>
                </div>
            </div>

            {schedule.terminalRuntimes && schedule.terminalRuntimes.length > 0 && (
                <div style={{ marginTop: '1.5rem', borderTop: '1px solid #eee', paddingTop: '1rem', minWidth: 0, overflow: 'hidden' }}>
                    <McAccordion>
                        <McAccordionItem>
                            <div slot="heading" style={{ fontSize: '0.85rem', color: '#333', fontWeight: 600 }}>
                                Per-terminal runtime windows ({schedule.terminalRuntimes.length})
                            </div>
                            <div slot="body" style={{ maxHeight: '250px', overflowY: 'auto', paddingRight: '8px', display: 'flex', flexDirection: 'column', gap: '8px', padding: '0.5rem 0' }}>
                                {schedule.terminalRuntimes.map((tr: TerminalRuntimeInfo) => (
                                    <div key={tr.terminalId} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '0.85rem', minWidth: 0 }}>
                                        <McIcon icon="file" size="16" color="#666" style={{ marginTop: '2px', flexShrink: 0 }} />
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                                <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tr.terminalId} • {tr.timezone}</span>
                                                <McTag
                                                    fit="small"
                                                    appearance={getStatusAppearance(tr.lastExecutionStatus || 'Scheduled to run')}
                                                    style={{ flexShrink: 0 }}
                                                >
                                                    {tr.lastExecutionStatus
                                                        ? (tr.lastExecutionStatus.startsWith('FAILED:') ? 'FAILED' : tr.lastExecutionStatus)
                                                        : 'Scheduled to run'}
                                                </McTag>
                                            </div>
                                            <div style={{ color: '#666', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                UTC {formatJavaUtcDateTime(tr.nextExecutionTimeUtc)} |
                                                Your time {tr.nextExecutionTimeUserLocal || 'N/A'}
                                            </div>
                                            {tr.lastExecutionStatus && tr.lastExecutionStatus.startsWith('FAILED:') && (
                                                <div style={{ color: 'var(--mds-color-error-text, #e20000)', fontSize: '0.8rem', marginTop: '2px', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                                    {tr.lastExecutionStatus}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </McAccordionItem>
                    </McAccordion>
                </div>
            )}

            {schedule.lastExecutionTime && (
                <div style={{ marginTop: '1.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>Last execution:</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.85rem', color: '#666' }}>Executed:</span>
                        <span style={{ fontSize: '0.85rem', color: '#444' }}>{schedule.executionCount ?? 0} times</span>
                        <McTag appearance={getStatusAppearance(schedule.lastExecutionStatus)} style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {schedule.lastExecutionStatus}
                        </McTag>
                    </div>
                </div>
            )}

            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                <McTag appearance="neutral" fit="small" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <McIcon icon="list-bullets" size="16" style={{ marginRight: '4px', flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Table: <strong>{schedule.resultTableName}</strong></span>
                </McTag>
                {!schedule.lastExecutionTime && (
                    <div style={{ fontSize: '0.8rem', color: '#888', marginLeft: 'auto', flexShrink: 0 }}>
                        Executed: {schedule.executionCount ?? 0} times
                    </div>
                )}
            </div>            <div slot="footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {isAdmin && (
                        <McTooltip>
                            <McButton
                                slot="trigger"
                                variant="plain"
                                onClick={() => onToggleEnabled(schedule.scheduleId, !schedule.enabled)}
                                style={{ color: schedule.enabled ? 'var(--mds-color-warning-text, #f57c00)' : 'var(--mds-color-success-text, #2e7d32)' }}
                            >
                                <McIcon icon={schedule.enabled ? "rotate" : "play"} />
                            </McButton>
                            <span slot="content">{schedule.enabled ? "Pause Schedule" : "Resume Schedule"}</span>
                        </McTooltip>
                    )}
                    
                    {isAdmin && (
                        <McTooltip>
                            <McButton slot="trigger" variant="plain" onClick={() => onEdit(schedule)}>
                                <McIcon icon="pencil" />
                            </McButton>
                            <span slot="content">Edit Schedule</span>
                        </McTooltip>
                    )}

                    <McTooltip>
                        <McButton slot="trigger" variant="plain" onClick={() => onViewResults(schedule)}>
                            <McIcon icon="eye" />
                        </McButton>
                        <span slot="content">View Stored Results</span>
                    </McTooltip>
                </div>

                {isAdmin && (
                    <McTooltip>
                        <McButton
                            slot="trigger"
                            variant="plain"
                            onClick={() => onDelete(schedule.scheduleId)}
                            style={{ color: 'var(--mds-color-error-text, #e20000)' }}
                        >
                            <McIcon icon="trash" />
                        </McButton>
                        <span slot="content">Delete Schedule</span>
                    </McTooltip>
                )}
            </div>
        </McCard>
    );
};
